import { useEffect, useRef, useState } from "react";
import type { CelldegaModel, Landscape } from "celldega";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { getS3PresignedDownloadUrl } from "~/lib/downloadS3File";
import { cn } from "~/lib/utils";
import type { ToolUIProps } from "~/pages/manifold-nextgen/components/conversations/ToolUIContext";
import { registerZipArchive } from "./zipArchiveFetch";

/** The tool card for `view_spatial_transcriptomics`: Celldega on a dataset's `*.celldega.zip`. */
export function CelldegaToolUI({ toolCallId, result, isRunning }: ToolUIProps) {
  const archive = parseArchiveResult(result);

  if (!archive) {
    return isRunning ? (
      <div role="status" aria-label="Loading spatial viewer">
        <Skeleton className={cn(VIEWER_HEIGHT_CLASS, "w-full")} />
      </div>
    ) : null;
  }
  return <CelldegaLandscape key={archive.filePath} viewerId={toolCallId} archive={archive} />;
}

function CelldegaLandscape({ viewerId, archive }: { viewerId: string; archive: CelldegaArchive }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    let cancelled = false;
    let landscape: Landscape | null = null;
    // Celldega fetches every LandscapeFiles path under this base URL; each request
    // is answered with a byte range of the presigned archive.
    const baseUrl = `${ARCHIVE_BASE}/${encodeURIComponent(viewerId)}`;
    const unregister = registerZipArchive({
      prefix: baseUrl,
      resolveArchiveUrl: () => getS3PresignedDownloadUrl(archive.filePath),
      archiveSize: archive.sizeBytes,
    });

    const mount = async () => {
      try {
        const [{ default: celldega }, imageSize] = await Promise.all([
          import("celldega"),
          readImageSize(baseUrl),
        ]);
        if (cancelled) {
          return;
        }
        const view = initialView(imageSize, container.clientWidth || DEFAULT_WIDTH, VIEWER_HEIGHT);
        const mounted = await celldega.landscape_ist(
          container,
          standaloneModel(),
          "",
          view.x,
          view.y,
          0,
          view.zoom,
          baseUrl,
          archive.name,
          0.25,
          0,
          VIEWER_HEIGHT
        );
        if (cancelled) {
          mounted.finalize();
          return;
        }
        landscape = mounted;
        setIsLoading(false);
      } catch (mountError) {
        if (cancelled) {
          return;
        }
        console.error("Celldega viewer failed to load", mountError);
        setError(mountError instanceof Error ? mountError.message : String(mountError));
        setIsLoading(false);
      }
    };
    void mount();

    return () => {
      cancelled = true;
      landscape?.finalize();
      unregister();
    };
  }, [viewerId, archive.filePath, archive.sizeBytes, archive.name]);

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not open the spatial viewer</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {/* The container stays laid out while loading so the initial view is fitted to its real width. */}
      <div className={cn("relative w-full", VIEWER_HEIGHT_CLASS, error && "hidden")}>
        <div
          ref={containerRef}
          aria-label={`Spatial viewer: ${archive.name}`}
          className={cn(
            "relative size-full overflow-hidden rounded-md border",
            isLoading && "invisible"
          )}
        />
        {isLoading && (
          <div role="status" aria-label="Loading spatial viewer" className="absolute inset-0">
            <Skeleton className="size-full" />
          </div>
        )}
      </div>
    </div>
  );
}

/** The fields of a `view_spatial_transcriptomics` result; anything else (an error) is null. */
function parseArchiveResult(result: string | undefined): CelldegaArchive | null {
  if (!result) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(result);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  const { file_path: filePath, size_bytes: sizeBytes, name } = parsed;
  if (typeof filePath !== "string" || typeof sizeBytes !== "number" || typeof name !== "string") {
    return null;
  }
  return { filePath, sizeBytes, name };
}

/**
 * The tissue image's size: `image_dimensions` when the parameters have it, otherwise the Size in the
 * DZI descriptor of the first image, which is where LandscapeFiles written without it keep it.
 */
async function readImageSize(baseUrl: string): Promise<ImageSize> {
  const response = await fetch(`${baseUrl}/landscape_parameters.json`);
  if (!response.ok) {
    throw new Error(`landscape_parameters.json: HTTP ${response.status}`);
  }
  const parameters: unknown = await response.json();
  if (!isRecord(parameters)) {
    throw new Error("landscape_parameters.json is not a JSON object");
  }
  const dimensions = parameters.image_dimensions;
  if (
    isRecord(dimensions) &&
    typeof dimensions.width === "number" &&
    typeof dimensions.height === "number"
  ) {
    return { imageWidth: dimensions.width, imageHeight: dimensions.height };
  }

  const imageName = firstImageName(parameters);
  if (!imageName) {
    throw new Error("landscape_parameters.json has neither image_dimensions nor image_info");
  }
  const dziPath = `pyramid_images/${encodeURIComponent(imageName)}.dzi`;
  const dzi = await fetch(`${baseUrl}/${dziPath}`);
  if (!dzi.ok) {
    throw new Error(`${dziPath}: HTTP ${dzi.status}`);
  }
  return parseDziSize(await dzi.text(), dziPath);
}

function firstImageName(parameters: Record<string, unknown>): string | null {
  const images = parameters.image_info;
  const first = Array.isArray(images) ? images[0] : undefined;
  return isRecord(first) && typeof first.name === "string" ? first.name : null;
}

function parseDziSize(xml: string, path: string): ImageSize {
  const descriptor = new DOMParser().parseFromString(xml, "application/xml");
  const size = descriptor.getElementsByTagName("Size")[0];
  const imageWidth = Number(size?.getAttribute("Width"));
  const imageHeight = Number(size?.getAttribute("Height"));
  if (!(imageWidth > 0) || !(imageHeight > 0)) {
    throw new Error(`${path} has no image Size`);
  }
  return { imageWidth, imageHeight };
}

/** Centre the image and zoom out until the whole of it fits the viewer. */
function initialView({ imageWidth, imageHeight }: ImageSize, width: number, height: number) {
  return {
    x: imageWidth / 2,
    y: imageHeight / 2,
    zoom: Math.log2(Math.min(width / imageWidth, height / imageHeight)),
  };
}

/** Celldega reads its traits from an anywidget model; outside Jupyter these are the defaults it needs. */
function standaloneModel(): CelldegaModel {
  const values = new Map<string, unknown>([
    ["region", {}],
    ["selected_cells", []],
    ["landmark_colors", {}],
    ["highlighted_genes", []],
    ["gene_list", []],
    ["front_end_query", {}],
  ]);
  return {
    get: (key) => values.get(key),
    set: (key, value) => {
      values.set(key, value);
    },
    save_changes: () => undefined,
    on: () => undefined,
    off: () => undefined,
    send: () => undefined,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// A host that never resolves, so a request that escapes the archive fails at once.
const ARCHIVE_BASE = "https://celldega-archive.invalid";
const VIEWER_HEIGHT = 560;
const VIEWER_HEIGHT_CLASS = "h-[560px]";
const DEFAULT_WIDTH = 800;

interface CelldegaArchive {
  filePath: string;
  sizeBytes: number;
  name: string;
}

interface ImageSize {
  imageWidth: number;
  imageHeight: number;
}
